import { StoreState } from '@/core/models/store.interface';
import { checkBackendVersion } from '@nxtpeople/nxt-vue-library';
import { Module } from 'vuex';
import { Tenant } from '../tenant/tenant.interface';
import { UserInfo, UserPermission } from './user-info.interface';
import { UserService } from './user.service';

const service = new UserService();

export interface UserState {
   users: Array<{ id: number; email: string; name: string; display_value: string; tenant: Tenant }>;
   user_info?: UserInfo;
   has_no_user_info: boolean;
}

const UserStore: Module<UserState, StoreState> = {
   namespaced: true,
   state: {
      users: [],
      user_info: undefined,
      has_no_user_info: false,
   },

   getters: {
      hasPermission: (state) => (scope_key: string, resource_type_key: string) => {
         return state.user_info?.user_permissions?.some(
            (permission: UserPermission) =>
               permission.scope_key == scope_key &&
               permission.resource_type_key.toLowerCase() == resource_type_key.toLowerCase() &&
               permission.is_allowed,
         );
      },
      getTeams(state) {
         return state.user_info?.teams;
      },
   },

   mutations: {
      setUsers(state, users) {
         state.users = users;
      },
      setUserInfo(state, user_info) {
         if (!user_info) state.has_no_user_info = true;
         state.user_info = user_info;
      },
      UPDATE_DEFAULT_TEAM(state, default_team_id) {
         if (!state.user_info) return;
         state.user_info!.default_team_id = default_team_id;
      },
      PATCH_USER_INFO(state, user_info) {
         state.user_info = { ...state.user_info, ...user_info };
      },
      setUserLanguage(state, language) {
         if (!state.user_info) return;
         state.user_info!.language = language;
      },
   },

   actions: {
      async get(context, { filter_team_id, search }: { filter_team_id?: number; search?: string }) {
         return await service.get(filter_team_id, search).then((response) => {
            context.commit('setUsers', response.data);
            return response.data;
         });
      },
      async getInfo(context) {
         return await service
            .getInfo()
            .catch(() => context.commit('setUserInfo', undefined))
            .then((response) => {
               if (!response) return;
               checkBackendVersion(response.version_number, 'nxt-plan-cache');
               context.commit('setUserInfo', response);
               return response;
            });
      },
      async patch(context, { path, value }: { path: string; value: any }) {
         const BODY = [
            {
               path,
               value,
               op: 'replace',
            },
         ];

         const USER_RESPONSE = await service.patch(context.state.user_info!.id, BODY);
         context.commit('PATCH_USER_INFO', USER_RESPONSE);
         return USER_RESPONSE;
      },
      async patchLanguage(context, { language_id }: { language_id: number }) {
         const USER_LANGUAGE = await service.patchLanguage(context.state.user_info!.id, language_id);
         context.commit('setUserLanguage', USER_LANGUAGE);
      },
   },
};

export default UserStore;
